type Query = {
  [key: string]: string;
};

interface Options extends RequestInit {
  query?: Query;
}

type OptionalBody = BodyInit | null;

const resolveURL = (path: string, query?: Query) => {
  let url: string = path;

  if (query) {
    const queryParam = new URLSearchParams(query);
    url += `?${queryParam.toString()}`;
  }

  return url;
};

const getOptions = (options: Options, token?: string) => {
  const defaultHeaders: HeadersInit = {
    Authorization: token ? `Bearer ${token}` : '',
  };

  let { body } = options;

  const isJSON = body != null && typeof body === 'object' && !(body instanceof FormData);

  if (isJSON) {
    defaultHeaders['Content-Type'] = 'application/json';
    body = JSON.stringify(body);
  }

  return {
    ...options,
    body,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };
};

async function fetchApi<T>(path: string, options: Options = {}) {
  const { query, ...opts } = options;

  const response = await fetch(resolveURL(path, query), getOptions(opts));
  const body = await response.json();
  const { status } = response;

  if (!status || status < 200 || status >= 300) {
    const error = new Error(body.error || response.statusText);

    throw error;
  }

  return body as T;
}

export default {
  fetch: fetchApi,
  delete: <T>(path: string, body?: OptionalBody, options?: RequestInit) =>
    fetchApi<T>(path, { body, ...options, method: 'DELETE' }),
  get: <T>(path: string, query?: Query, options?: RequestInit) =>
    fetchApi<T>(path, { query, ...options, method: 'GET' }),
  post: <T>(path: string, body?: OptionalBody, options?: RequestInit) =>
    fetchApi<T>(path, { body, ...options, method: 'POST' }),
  put: <T>(path: string, body?: OptionalBody, options?: RequestInit) =>
    fetchApi<T>(path, { body, ...options, method: 'PUT' }),
};
